
/**
 * Centralized logging utility for consistent logging across the application
 * Provides different log levels and can be easily configured for production
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
}

class Logger {
  private config: LogConfig = {
    level: 'info',
    enableConsole: true,
    enableRemote: false
  };

  configure(config: Partial<LogConfig>) {
    this.config = { ...this.config, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };
    return levels[level] >= levels[this.config.level];
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const dataStr = data ? ` ${JSON.stringify(data)}` : '';
    return `${timestamp} ${level}: ${message}${dataStr}`;
  }

  debug(message: string, data?: any) {
    if (this.shouldLog('debug') && this.config.enableConsole) {
      console.debug(this.formatMessage('debug', message, data));
    }
  }

  info(message: string, data?: any) {
    if (this.shouldLog('info') && this.config.enableConsole) {
      console.info(this.formatMessage('info', message, data));
    }
  }

  warn(message: string, data?: any) {
    if (this.shouldLog('warn') && this.config.enableConsole) {
      console.warn(this.formatMessage('warn', message, data));
    }
  }

  error(message: string, error?: Error | any) {
    if (this.shouldLog('error') && this.config.enableConsole) {
      console.error(this.formatMessage('error', message, error));
    }
  }

  // Specific logging methods for common use cases
  courseNavigation(action: string, courseId: string, moduleId?: string) {
    this.info(`Course Navigation: ${action}`, { courseId, moduleId });
  }

  quizCompletion(moduleId: string, score: number, passed: boolean) {
    this.info(`Quiz Completed: ${moduleId}`, { score, passed });
  }

  authEvent(event: string, userId?: string) {
    this.info(`Auth Event: ${event}`, { userId });
  }

  practiceComplete(moduleId: string, success: boolean) {
    this.info(`Practice Completed: ${moduleId}`, { success });
  }
}

export const logger = new Logger();

// Configure for development/production
if (process.env.NODE_ENV === 'production') {
  logger.configure({ level: 'warn' });
} else {
  logger.configure({ level: 'debug' });
}
